import { animate, style, transition, trigger } from '@angular/animations';
import { AsyncPipe } from '@angular/common';
import {
  AfterContentInit,
  Component,
  DOCUMENT,
  ElementRef,
  HostListener,
  NgZone,
  OnDestroy,
  OnInit,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import { RouterLink } from '@angular/router';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { BurgerMenuContent, CategoryBurgerLink, Image, LinkContent, SubNavigationContent } from 'src/app/models';
import { BasketQuery } from 'src/app/services/basket/basket.query';
import { BasketService } from 'src/app/services/basket/basket.service';
import { CategoryService } from 'src/app/services/category/category.service';
import { ContentService, headerSections, sections } from 'src/app/services/content/content.service';
import { LayoutService } from 'src/app/services/device-detector/layout.service';
import { MobileService } from 'src/app/services/mobile.service';
import { ThemeService } from 'src/app/services/theme/theme.service';
import { environment } from 'src/environments/environment';
import { buttonIcons, symbolIcons } from '../../../modules/icon.module';
import { IconButtonComponent } from '../../components';
import { MemberBarComponent } from '../../components/auth';
import { ContentImagePipe } from '../../pipes/cms-image.pipe';
import { ContentSection } from '../content-section';

@Component({
  selector: 'app-online-header',
  templateUrl: './online-header.component.html',
  styleUrls: ['./online-header.component.scss'],
  animations: [
    trigger('installAnimation', [
      transition(':enter', [style({ opacity: 0, width: 0 }), animate('800ms ease-out', style({ opacity: 1, width: '*' }))]),
      transition(':leave', [style({ opacity: 1, width: '*' }), animate('800ms ease-in', style({ opacity: 0, width: 0 }))]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [RouterLink, MemberBarComponent, IconButtonComponent, ContentImagePipe, MatBadgeModule, AsyncPipe],
})
export class OnlineHeaderComponent extends ContentSection implements OnInit, AfterContentInit, OnDestroy {
  private basketService = inject(BasketService);
  private basketQuery = inject(BasketQuery);
  private mobileService = inject(MobileService);
  private themeService = inject(ThemeService);
  private categoryService = inject(CategoryService);
  private deviceService = inject(DeviceDetectorService);
  private layoutService = inject(LayoutService);
  private elRef = inject(ElementRef);
  private document = inject<Document>(DOCUMENT);
  private ngZone = inject(NgZone);

  subscription: Subscription;

  contactUsLink = `/${environment.urls.content.contact_us}`;
  filterIcon = buttonIcons.filter;
  mobileMenuIcon = buttonIcons.menu;

  searchPlaceholder = '';
  searchButton = symbolIcons.search;

  links: (SubNavigationContent | LinkContent | BurgerMenuContent | CategoryBurgerLink)[];
  headerLinks: LinkContent[];
  openNavigationName?: string;
  openNavigationRef: HTMLElement;
  closeSubNavTimeout: any;

  storeLogo: Image;

  basketIcon = buttonIcons.basket;
  count$: Observable<number>;

  isMobile = false;
  menuIcon: string;
  storeClosed: boolean;
  menuLabel: string;

  enableMembers: boolean;

  @HostListener('focusout', ['$event']) onFocusOut(event: FocusEvent) {
    if (!this.openNavigationRef?.contains(event.relatedTarget as any) && this.document.hasFocus()) {
      this.closeNavigation();
    }
  }

  @HostListener('mouseenter', ['$event']) onMouseEnter(event: MouseEvent) {
    clearTimeout(this.closeSubNavTimeout);
  }

  @HostListener('mouseleave', ['$event']) onMouseLeave(event: MouseEvent) {
    this.ngZone.runOutsideAngular(() => {
      this.closeSubNavTimeout = setTimeout(() => {
        this.closeNavigation();
      }, 2000);
    });
  }

  constructor() {
    const contentService = inject(ContentService);
    super(contentService, sections.header);

    this.enableMembers = this.contentService.getMerchant().enableMembers;
    this.storeClosed = this.contentService.getStoreClosed();

    this.menuLabel = this.content.get('deliveryPickup');
    this.storeLogo = { src: this.content.get('headerImage'), alt: this.contentService.getTitle() };
    this.searchPlaceholder = this.content.get('searchPlaceholder') || 'searchPlaceholder';

    this.checkScreenSize();

    // TODO: add accessability
    const { header, navigation } = headerSections;

    const headerContent = this.contentService.getMerchantContent(header);
    this.headerLinks = [];
    for (const entry of headerContent) {
      switch (entry.type) {
        case 'simple': {
          const link = this.contentService.getContentLink(entry);
          this.headerLinks.push(link);
          break;
        }
        case 'link':
          this.headerLinks.push(entry);
          break;
      }
    }

    const navigationContent = this.contentService.getMerchantContent(navigation);
    this.links = [];
    for (const entry of navigationContent) {
      switch (entry.type) {
        case 'simple': {
          const link = this.contentService.getContentLink(entry);
          this.links.push(link);
          break;
        }
        case 'categoryBurger': {
          const link = this.categoryService.buildCategoryBurgerMenu(entry);
          if (link) this.links.push(link);
          break;
        }
        case 'link':
        case 'subNav':
        case 'burger':
          this.links.push(entry);
          break;
      }
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.checkScreenSize();
  }

  checkScreenSize() {
    switch (true) {
      case this.layoutService.isMobile:
      case this.layoutService.isTablet:
        this.menuIcon = buttonIcons.menu;
        this.isMobile = true;
        break;
      default:
        this.menuIcon = buttonIcons.shipping;
        this.isMobile = false;
    }
  }

  ngOnInit() {
    this.subscription = new Subscription();

    this.count$ = this.basketQuery.selectProductLines$.pipe(
      map((productLines) => {
        let count = 0;
        productLines.forEach((line) => (count += line.quantity));
        return count;
      }),
    );
  }

  ngAfterContentInit() {
    this.themeService.subscribeComponent(this.elRef);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  openNavigation(el: HTMLElement, navigation: string) {
    if (this.openNavigationRef) {
      this.openNavigationRef.blur();
    }

    this.openNavigationName = navigation;
    this.openNavigationRef = el;
  }

  closeNavigation() {
    this.openNavigationName = undefined;
  }

  sameSite(link: LinkContent) {
    return this.contentService.sameSite(link);
  }

  openMobileMenu() {
    this.mobileService.setOverlay(true);
  }

  openBasket() {
    this.basketService.setOverlay(true);
  }
}
